import Popper from "popper.js"
import { escapeHTML } from "../helpers"

const TYPES = ["tooltip", "popover"]

const tooltips = new WeakMap()

export class Tooltip {
  static install(element, options) {
    this.uninstall(element)
    const tooltip = new this(element, options)
    tooltip.install()
    tooltips.set(element, tooltip)
    return tooltip
  }

  static uninstall(element) {
    const tooltip = tooltips.get(element)
    if (!tooltip) return
    tooltip.uninstall()
    tooltips.delete(element)
  }

  constructor(element, { placement, text, html, type } = {}) {
    this.element = element
    this.options = { placement, text, html, type: TYPES.includes(type) ? type : TYPES[0] }
    this.id = `tooltip-${Math.random().toString(16).slice(-8)}`
  }

  install() {
    if (this.installed) return
    this.installed = true
    this.element.addEventListener("mouseenter", this.show)
    this.element.addEventListener("focusin", this.show)
    this.element.addEventListener("mouseleave", this.hide)
    this.element.addEventListener("focusout", this.hide)
    this.tipElement.addEventListener("transitionend", this.removeIfHidden)
  }

  uninstall() {
    if (!this.installed) return
    this.installed = false
    this.element.removeEventListener("mouseenter", this.show)
    this.element.removeEventListener("focusin", this.show)
    this.element.removeEventListener("mouseleave", this.hide)
    this.element.removeEventListener("focusout", this.hide)
    this.tipElement.removeEventListener("transitionend", this.removeIfHidden)
    this.tipElement.remove()
    this.destroyPopper()
    this.hide()
  }

  show = () => {
    if (this.isVisible) return
    if (!this.shouldShow) return
    this.tipElement.classList.add("show")
    const title = this.element.getAttribute("title")
    this.element.removeAttribute("title")
    this.element.setAttribute("aria-label", title)
    this.element.setAttribute("aria-describedby", this.id)
    document.body.append(this.tipElement)
    this.createOrUpdatePopper()
  }

  hide = () => {
    if (this.isHidden) return
    this.tipElement.classList.remove("show")
    const title = this.element.getAttribute("aria-label")
    this.element.removeAttribute("aria-label")
    this.element.setAttribute("title", title)
    this.element.removeAttribute("aria-describedby")
  }

  // Private

  removeIfHidden = () => {
    if (this.isHidden) this.tipElement.remove()
  }

  createOrUpdatePopper() {
    if (this.popper) {
      this.popper.update()
    } else {
      this.popper = new Popper(this.element, this.tipElement, this.popperConfig)
    }
  }

  destroyPopper() {
    if (!this.popper) return
    this.popper.destroy()
    delete this.popper
  }

  get popperConfig() {
    return {
      placement: this.placement,
      modifiers: {
        arrow: { element: ".arrow" },
      },
      onCreate: ({ placement }) => {
        this.tipElement.classList.add(`sh-${this.type}-${placement}`, `bs-${this.type}-${placement}`)
        this.currentPlacement = placement
      },
      onUpdate: ({ placement }) => {
        if (placement == this.currentPlacement) return
        this.tipElement.classList.remove(
          `sh-${this.type}-${this.currentPlacement}`,
          `bs-${this.type}-${this.currentPlacement}`
        )
        this.tipElement.classList.add(`sh-${this.type}-${placement}`, `bs-${this.type}-${placement}`)
        this.currentPlacement = placement
      },
    }
  }

  get type() {
    return this.options.type
  }

  get text() {
    return this.options.text || this.element.getAttribute("title") || this.element.getAttribute("aria-label") || ""
  }

  get html() {
    return this.options.html || this.text
  }

  get placement() {
    return this.options.placement || this.element.getAttribute("data-tooltip") || "top"
  }

  get tipElement() {
    const value = createElement(this.tipHTML)
    Object.defineProperty(this, "tipElement", { value })
    return value
  }

  get tipHTML() {
    const classes = `sh-${this.type} sh-${this.type}-${this.placement} ${this.type} bs-${this.type}-${this.placement} fade`

    return `
      <div class="${classes}" role="tooltip" id="${this.id}">
        <div class="arrow"></div>
        ${
          this.type == "popover"
            ? `<h3 class="popover-header"></h3><div class="sh-popover-content popover-body">${this.html}</div>`
            : `<div class="tooltip-inner">${escapeHTML(this.text)}</div>`
        }
      </div>
    `
  }

  get shouldShow() {
    return this.shouldOnlyShownWhenTruncated ? this.isTruncated : true
  }

  get shouldOnlyShownWhenTruncated() {
    return this.element.hasAttribute("data-tooltip-if-truncated")
  }

  get isTruncated() {
    return this.element.scrollWidth > this.element.clientWidth
  }

  get isVisible() {
    return this.tipElement.classList.contains("show")
  }

  get isHidden() {
    return !this.isVisible
  }
}

function createElement(html = "") {
  const template = document.createElement("template")
  template.innerHTML = html
  return template.content.firstElementChild
}
